import { Area } from "react-easy-crop";
import { ErrorCompression, ErrorCreateCanvas, ErrorInvalidSVG, ErrorSVGExceedSize } from "./type";

export function readFile(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result as string), false);
    reader.readAsDataURL(file); // read as base64-encoded
  });
}

export const createImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

function toBlobPromise(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}

async function resizeImage(canvas: HTMLCanvasElement, targetSizeKB: number): Promise<string> {
  const targetSizeBytes = targetSizeKB * 1024;
  let quality = 1;
  const qualityStep = 0.05;
  let compressedBlob: Blob | null = null;
  let currentSize = Infinity;

  while (currentSize > targetSizeBytes && quality > 0.0) {
    compressedBlob = await toBlobPromise(canvas, quality);
    if (compressedBlob === null) {
      throw ErrorCompression;
    }
    currentSize = compressedBlob.size;

    if (currentSize > targetSizeBytes) {
      quality = Math.max(0.0, quality - qualityStep);
    }
  }

  if (compressedBlob && currentSize <= targetSizeBytes) {
    return URL.createObjectURL(compressedBlob);
  }
  throw ErrorCompression;
}

export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  desiredDimension?: { width: number; height: number },
  targetSizeKB: number = Infinity
): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw ErrorCreateCanvas;
  }

  canvas.width = image.width;
  canvas.height = image.height;
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");

  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    throw ErrorCreateCanvas;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = desiredDimension?.width ?? pixelCrop.width;
  croppedCanvas.height = desiredDimension?.height ?? pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    desiredDimension?.width ?? pixelCrop.width,
    desiredDimension?.height ?? pixelCrop.height
  );

  return resizeImage(croppedCanvas, targetSizeKB);
}

export async function getCroppedSVG(
  imageSrc: string, // Expects data:image/svg+xml;...
  pixelCrop: Area,
  desiredDimension?: { width: number; height: number },
  targetSizeKB: number = Infinity
): Promise<string> {
  let svgString: string;
  const parts = imageSrc.split(",");
  const meta = parts[0]; // "data:image/svg+xml;base64" or "data:image/svg+xml"
  const data = parts[1];

  if (meta.includes("base64")) {
    svgString = atob(data); // Decode base64
  } else {
    svgString = decodeURIComponent(data); // Decode URL-encoded
  }

  // Parse the SVG string into an XML Document
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
  const svgElement = svgDoc.documentElement;

  if (svgElement.tagName.toLowerCase() !== "svg") {
    throw ErrorInvalidSVG;
  }

  // Set the new viewBox to the crop area
  const newViewBox = `${pixelCrop.x} ${pixelCrop.y} ${pixelCrop.width} ${pixelCrop.height}`;
  svgElement.setAttribute("viewBox", newViewBox);

  // Set the output diensions
  const outputWidth = desiredDimension?.width ?? pixelCrop.width;
  const outputHeight = desiredDimension?.height ?? pixelCrop.height;
  svgElement.setAttribute("width", String(outputWidth));
  svgElement.setAttribute("height", String(outputHeight));

  // Serialize the modified SVG back to a string
  const serializer = new XMLSerializer();
  const croppedSvgString = serializer.serializeToString(svgElement);

  // Create a Blob and Object URL
  const blob = new Blob([croppedSvgString], { type: "image/svg+xml" });

  // Throw error if svg is too large
  const targetSizeBytes = targetSizeKB * 1024;
  if (blob.size > targetSizeBytes) {
    throw ErrorSVGExceedSize;
  }

  return URL.createObjectURL(blob);
}
